import { useRef, useState, useCallback } from 'react';
import { cn } from '@/lib/utils';
import type { ClipSegment } from '@/types/job';

interface VideoTimelineProps {
  segments: ClipSegment[];
  videoDuration: number;
  currentTime?: number;
  onSeek?: (time: number) => void;
  onSegmentClick?: (segment: ClipSegment) => void;
  className?: string;
}

function formatTimecode(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  const f = Math.floor((seconds % 1) * 100);
  return `${m}:${s.toString().padStart(2, '0')}.${f.toString().padStart(2, '0')}`;
}

const SEGMENT_COLORS = ['bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-violet-500', 'bg-rose-500', 'bg-cyan-500'];

export default function VideoTimeline({
  segments,
  videoDuration,
  currentTime = 0,
  onSeek,
  onSegmentClick,
  className,
}: VideoTimelineProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [hoveredSegment, setHoveredSegment] = useState<ClipSegment | null>(null);
  const [hoverTime, setHoverTime] = useState<number | null>(null);
  const [hoverX, setHoverX] = useState(0);

  const handleTrackClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!trackRef.current || !onSeek || videoDuration <= 0) return;
      const rect = trackRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const ratio = x / rect.width;
      onSeek(ratio * videoDuration);
    },
    [onSeek, videoDuration],
  );

  const handleTrackHover = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!trackRef.current || videoDuration <= 0) return;
      const rect = trackRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const ratio = x / rect.width;
      setHoverTime(ratio * videoDuration);
      setHoverX(x);
    },
    [videoDuration],
  );

  if (videoDuration <= 0) return null;

  const playheadPercent = (currentTime / videoDuration) * 100;

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{formatTimecode(currentTime)}</span>
        <span>
          {segments.length} scene{segments.length !== 1 ? 's' : ''} detected
        </span>
        <span>{formatTimecode(videoDuration)}</span>
      </div>

      <div
        ref={trackRef}
        className="relative h-10 bg-muted rounded cursor-pointer group"
        onClick={handleTrackClick}
        onMouseMove={handleTrackHover}
        onMouseLeave={() => {
          setHoverTime(null);
          setHoveredSegment(null);
        }}
      >
        {segments.map((seg, i) => {
          const left = (seg.startTime / videoDuration) * 100;
          const width = ((seg.endTime - seg.startTime) / videoDuration) * 100;
          const color = SEGMENT_COLORS[i % SEGMENT_COLORS.length];

          return (
            <div
              key={seg.index}
              className={cn(
                'absolute top-0 h-full rounded-sm opacity-70 hover:opacity-100 transition-opacity cursor-pointer',
                color,
                hoveredSegment?.index === seg.index && 'opacity-100 ring-2 ring-white',
              )}
              style={{
                left: `${left}%`,
                width: `${Math.max(width, 0.5)}%`,
              }}
              onMouseEnter={() => setHoveredSegment(seg)}
              onMouseLeave={() => setHoveredSegment(null)}
              onClick={(e) => {
                e.stopPropagation();
                onSegmentClick?.(seg);
                onSeek?.(seg.startTime);
              }}
            />
          );
        })}

        <div
          className="absolute top-0 h-full w-0.5 bg-white shadow-md z-10 pointer-events-none"
          style={{ left: `${playheadPercent}%` }}
        />

        {hoverTime !== null && (
          <div
            className="absolute -top-7 -translate-x-1/2 bg-foreground text-background text-xs px-1.5 py-0.5 rounded pointer-events-none z-20"
            style={{ left: `${hoverX}px` }}
          >
            {formatTimecode(hoverTime)}
          </div>
        )}
      </div>

      {segments.length > 0 && (
        <div className="space-y-1">
          {segments.map((seg, i) => {
            const color = SEGMENT_COLORS[i % SEGMENT_COLORS.length];
            const isHovered = hoveredSegment?.index === seg.index;

            return (
              <button
                key={seg.index}
                className={cn(
                  'flex items-center gap-2 w-full text-left px-2 py-1 rounded text-xs transition-colors',
                  isHovered ? 'bg-muted' : 'hover:bg-muted/50',
                )}
                onMouseEnter={() => setHoveredSegment(seg)}
                onMouseLeave={() => setHoveredSegment(null)}
                onClick={() => {
                  onSegmentClick?.(seg);
                  onSeek?.(seg.startTime);
                }}
              >
                <div className={cn('w-2.5 h-2.5 rounded-sm flex-shrink-0', color)} />
                <span className="text-muted-foreground font-mono">
                  {formatTimecode(seg.startTime)} — {formatTimecode(seg.endTime)}
                </span>
                <span className="truncate">{seg.label}</span>
                <span className="ml-auto text-muted-foreground">{(seg.confidence * 100).toFixed(0)}%</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
